/**
 * BuyerJourneyOrchestrator (AJIP-16.02.007 · Additional Founder Directives)
 * Sole authorised Buyer Journey composition layer. Coordinates published
 * services only. Never duplicates Registration, Discovery, Eligibility,
 * Resolution, Activation, Core Engine state management, Persistence, or
 * Journey Commands.
 *
 * Contains Buyer-specific configuration and orchestration only.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { FlagDisabledError } from '../core/JourneyErrors';
import { buildBuyerDefinition } from './BuyerJourneyDefinition';
import {
  BUYER_STAGES, BUYER_EVENTS, findBuyerTransition, STAGE_TO_ENGINE_STATE,
} from './BuyerJourneyModel';
import { validateBuyerContext } from './BuyerJourneyContextContract';
import {
  BUYER_JOURNEY_EVENTS, BuyerJourneyObservability,
} from './BuyerJourneyObservability';
import {
  InvalidBuyerTransitionError, BuyerJourneyNotActiveError,
} from './BuyerJourneyErrors';
import { fnv1a } from '../persistence/Hash';

const BUYER_JOURNEY_ID = 'buyer';
const BUYER_JOURNEY_VERSION = 2;

const _requireEnabled = (svc) => {
  if (!svc._enabled) throw new FlagDisabledError('JOURNEYS_ENABLED is false. BuyerJourneyOrchestrator is inert.');
};

/**
 * Additional Founder Directive — BUYER JOURNEY INSTANCE IDENTITY.
 * Each independent sourcing objective gets a distinct instance identity,
 * derived deterministically from principal + sourcing objective, and
 * separate from the journey definition identity.
 */
const buildBuyerInstanceId = ({ principalRef, sourcingObjective, correlationRef }) => {
  const principal = principalRef == null ? 'anon' : String(principalRef);
  const objective = sourcingObjective == null ? '' : String(sourcingObjective);
  const corr = correlationRef == null ? '' : String(correlationRef);
  const material = `${principal}::${objective}::${corr}`;
  return `bji_${fnv1a(material)}`;
};

class BuyerJourneyOrchestrator {
  constructor({
    engine, registrationService, discoveryService,
    resolutionService, activationService, journeyCommands,
    persistenceService = null, observability = null,
    nowFn = () => Date.now(),
  } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    if (this._enabled) {
      if (!engine) throw new Error('BuyerJourneyOrchestrator requires engine');
      if (!registrationService) throw new Error('requires registrationService');
      if (!discoveryService) throw new Error('requires discoveryService');
      if (!resolutionService) throw new Error('requires resolutionService');
      if (!activationService) throw new Error('requires activationService');
      if (!journeyCommands) throw new Error('requires journeyCommands');
    }
    this._engine = engine || null;
    this._reg = registrationService || null;
    this._disc = discoveryService || null;
    this._res = resolutionService || null;
    this._act = activationService || null;
    this._cmd = journeyCommands || null;
    this._persistence = persistenceService;
    this._obs = observability instanceof BuyerJourneyObservability
      ? observability : new BuyerJourneyObservability();
    this._now = nowFn;
    this._instances = new Map(); // instanceId -> { principalRef, currentStage }
    this._registered = false;
  }

  get enabled() { return this._enabled; }
  get observability() { return this._obs; }
  static get JOURNEY_ID() { return BUYER_JOURNEY_ID; }
  static get JOURNEY_VERSION() { return BUYER_JOURNEY_VERSION; }

  /**
   * Register the enriched v2 buyer definition via published
   * RegistrationService.replace() path (Founder Ruling B1).
   */
  register() {
    _requireEnabled(this);
    const def = buildBuyerDefinition({ version: BUYER_JOURNEY_VERSION });
    const status = this._reg.getRegistrationStatus(BUYER_JOURNEY_ID);
    if (status && status.journeyVersion < BUYER_JOURNEY_VERSION) {
      this._reg.replace(def);
    } else if (!status) {
      this._reg.register(def);
    }
    this._registered = true;
    this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_REGISTRATION_CONFIRMED, {
      journeyId: BUYER_JOURNEY_ID, journeyVersion: BUYER_JOURNEY_VERSION,
    });
    return def;
  }

  /**
   * Build a Buyer Resolution Request (WS10). Returns a valid request
   * shape consumable by the ResolutionService.
   */
  buildResolutionRequest({ requestId, principalRef, sourcingObjective, correlationRef = null, discoveryContext = {} }) {
    validateBuyerContext(discoveryContext);
    return {
      requestId,
      correlationRef,
      canonReference: 'AJIP-16.02.007',
      journeyId: BUYER_JOURNEY_ID,
      journeyVersion: BUYER_JOURNEY_VERSION,
      principalContext: { principalRef },
      discoveryContext: {
        authenticated: true, permissions: [],
        activeFeatureFlags: ['JOURNEYS_ENABLED'],
        audience: 'authorised-buyer',
        completedJourneys: [], activeJourneys: [],
        ...discoveryContext,
      },
      entryPoint: 'internal-orchestration',
      requiredFeatureFlags: ['JOURNEYS_ENABLED'],
      activationIntent: true,
      metadata: { sourcingObjective, instanceId: buildBuyerInstanceId({ principalRef, sourcingObjective, correlationRef }) },
    };
  }

  /**
   * Activate a Buyer Journey instance for a specific sourcing objective.
   * Uses only published Resolution → Activation pathway.
   */
  async activate({ requestId, principalRef, sourcingObjective, correlationRef = null, discoveryContext = {} }) {
    _requireEnabled(this);
    this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_ACTIVATION_REQUESTED, { requestId, principalRef });
    const request = this.buildResolutionRequest({ requestId, principalRef, sourcingObjective, correlationRef, discoveryContext });
    const result = await this._act.activate(request);
    if (result.status === 'ACTIVATED' || result.status === 'RESUMED' || result.status === 'RESTORED') {
      const instanceId = request.metadata.instanceId;
      this._instances.set(instanceId, {
        principalRef, sourcingObjective, correlationRef,
        currentStage: BUYER_STAGES.INTENT_CAPTURED,
      });
      // Establish initial stage through the protected Core Engine
      // extension `updateStage()` (Founder Ruling B8).
      this._engine.updateStage(BUYER_JOURNEY_ID, BUYER_STAGES.INTENT_CAPTURED);
      this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_ACTIVATED, {
        requestId, instanceId, initialStage: BUYER_STAGES.INTENT_CAPTURED,
      });
    } else {
      this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_ACTIVATION_REJECTED, {
        requestId, status: result.status, reason: result.reason,
      });
    }
    return result;
  }

  /**
   * Advance the buyer journey to the next stage via a deterministic
   * transition table. Founder Ruling B8: uses `engine.updateStage()`,
   * NEVER JourneyContext._setStage() directly.
   */
  advanceStage({ buyerEvent, requestId = null }) {
    _requireEnabled(this);
    const ctx = this._engine.contextOf(BUYER_JOURNEY_ID);
    if (!ctx) throw new BuyerJourneyNotActiveError(`Buyer journey '${BUYER_JOURNEY_ID}' is not active`);
    const fromStage = ctx.currentStage || BUYER_STAGES.INTENT_CAPTURED;
    const transition = findBuyerTransition(fromStage, buyerEvent);
    if (!transition) {
      throw new InvalidBuyerTransitionError(
        `No buyer transition from '${fromStage}' on event '${buyerEvent}'`,
      );
    }
    // Apply engine state transition where required (e.g. wait/resume/complete).
    if (transition.engineTransition) {
      switch (transition.engineTransition) {
        case 'wait': this._engine.suspend(BUYER_JOURNEY_ID); break;
        case 'resume': this._engine.resume(BUYER_JOURNEY_ID); break;
        case 'complete': this._engine.complete(BUYER_JOURNEY_ID); break;
        default: break;
      }
    }
    // Update stage via the AUTHORISED Core Engine public extension.
    this._engine.updateStage(BUYER_JOURNEY_ID, transition.to);
    this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_STAGE_TRANSITIONED, {
      requestId, fromStage, toStage: transition.to, buyerEvent,
      engineState: STAGE_TO_ENGINE_STATE[transition.to],
      persist: Boolean(transition.persist),
    });
    return Object.freeze({
      journeyId: BUYER_JOURNEY_ID,
      fromStage, toStage: transition.to,
      buyerEvent,
      engineState: STAGE_TO_ENGINE_STATE[transition.to],
    });
  }

  /**
   * Complete via the protected Journey Command Boundary.
   */
  complete({ requestId, principalRef, correlationRef = null }) {
    _requireEnabled(this);
    const r = this._cmd.complete({
      requestId, journeyId: BUYER_JOURNEY_ID, principalRef, correlationRef,
    });
    this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_COMPLETED, { requestId, result: r });
    return r;
  }

  cancel({ requestId, principalRef, correlationRef = null }) {
    _requireEnabled(this);
    const r = this._cmd.cancel({
      requestId, journeyId: BUYER_JOURNEY_ID, principalRef, correlationRef,
    });
    this._obs.emit(BUYER_JOURNEY_EVENTS.BUYER_JOURNEY_CANCELLED, { requestId, result: r });
    return r;
  }

  isRegistered() { return this._registered; }
}

export { BuyerJourneyOrchestrator, buildBuyerInstanceId, BUYER_JOURNEY_ID, BUYER_JOURNEY_VERSION };
